import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  FiGrid,
  FiChevronDown,
  FiLogOut,
  FiLayers,
  FiPackage,
  FiShoppingBag,
  FiUserCheck,
  FiDollarSign,
  FiBarChart2,
  FiSettings,
  FiTag,
  FiFileText,
} from "react-icons/fi";
import { Toolbox } from "lucide-react";
import logo from "../../assets/logo.png";
import { useAuth } from "../../context/AuthContext";
import { hasAnyPermission, isProductionHiddenTool, ATTENDANCE_PAGE_PERMISSIONS } from "../../utils/permissions";

const menuItems = [
  {
    title: 'Accounting',
    icon: <FiDollarSign size={20}/>,
    children: [
      {title:'Company & GST',path:'/admindashboard/accounting/setup',permissions:['accounting.view']},
      {title:'Chart of Accounts',path:'/admindashboard/accounting/ledgers',permissions:['accounting.view']},
      {title:'Vouchers',path:'/admindashboard/accounting/vouchers',permissions:['accounting.view']},
      {title:'Trial Balance',path:'/admindashboard/accounting/trial',permissions:['accounting.view']},
      {title:'Ledger Statement',path:'/admindashboard/accounting/ledger-statement',permissions:['accounting.view']},
      {title:'Party Accounts & Outstanding',path:'/admindashboard/accounting/party-accounts',permissions:['accounting.view']},
      {title:'Posting Setup',path:'/admindashboard/accounting/posting-setup',permissions:['accounting.view']},
      {title:'Sales Posting Setup',path:'/admindashboard/accounting/sales-setup',permissions:['accounting.view']},
      {title:'Sales Snapshot Review',path:'/admindashboard/accounting/sales-snapshots',permissions:['accounting.view']},
      {title:'Card & UPI Clearing',path:'/admindashboard/accounting/tender-clearing',permissions:['accounting.view']},
      {title:'Purchase Invoices',path:'/admindashboard/accounting/purchase-invoices',permissions:['accounting.view']},
      {title:'SIS Supplier Liabilities',path:'/admindashboard/accounting/sis-liabilities',permissions:['accounting.view']},
      {title:'Supplier Returns',path:'/admindashboard/accounting/supplier-returns',permissions:['accounting.view']},
    ],
  },
  {
    title: "Dashboard",
    path: "/admindashboard",
    icon: <FiGrid size={20} />,
    permissions: [],
  },
  {
    title: "Masters",
    icon: <FiLayers size={20} />,
    children: [
      {
        title: "Master Dashboard",
        path: "/admindashboard/masters",
        permissions: [
          "brands.view",
          "categories.view",
          "subcategories.view",
          "colors.view",
          "sizes.view",
          "units.view",
          "seasons.view",
          "products.view",
          "suppliers.view",
          "stores.view",
        ],
      },
      {
        title: "Brands",
        path: "/admindashboard/masters/brands",
        permissions: ["brands.view"],
      },
      {
        title: "HSN & GST",
        path: "/admindashboard/masters/tax-catalog",
        permissions: ["tax_catalog.view"],
      },
      {
        title: "Categories",
        path: "/admindashboard/masters/categories",
        permissions: ["categories.view"],
      },
      {
        title: "Size Groups",
        path: "/admindashboard/masters/sizes/groups",
        permissions: ["sizesgroups.view"],
      },
      {
        title: "Sub Categories",
        path: "/admindashboard/masters/sub-categories",
        permissions: ["subcategories.view"],
      },
      {
        title: "Sizes",
        path: "/admindashboard/masters/sizes",
        permissions: ["sizes.view"],
      },
      {
        title: "Units",
        path: "/admindashboard/masters/units",
        permissions: ["units.view"],
      },
      {
        title: "Colors",
        path: "/admindashboard/masters/colors",
        permissions: ["colors.view"],
      },
      {
        title: "Seasons",
        path: "/admindashboard/masters/seasons",
        permissions: ["seasons.view"],
      },
      {
        title: "Products",
        path: "/admindashboard/masters/products",
        permissions: ["products.view"],
      },
      {
        title: "Suppliers",
        path: "/admindashboard/masters/suppliers",
        permissions: ["suppliers.view"],
      },
      {title:'Commercial Agreements',path:'/admindashboard/masters/commercial-agreements',permissions:['suppliers.view']},
      {
        title: "Stores",
        path: "/admindashboard/masters/stores",
        permissions: ["stores.view"],
      },
      {
        title: "Counters",
        path: "/admindashboard/pos/counters",
        permissions: ["pos.manage_counters"],
      },
      {
        title: "Users",
        path: "/admindashboard/masters/users",
        permissions: ["users.view"],
      },
    ],
  },
  {
    title: "Inventory",
    icon: <FiPackage size={20} />,
    children: [
      {
        title: "Overview",
        path: "/admindashboard/inventory",
        permissions: ["inventory.view"],
      },
      {
        title: "Stock Ledger",
        path: "/admindashboard/inventory/ledger",
        permissions: ["inventory.view"],
      },
      {
        title: "Stock Adjustment",
        path: "/admindashboard/inventory/adjustment",
        permissions: ["inventory.adjust"],
      },
      {
        title: "Stock Transfers",
        path: "/admindashboard/inventory/transfers",
        permissions: ["inventory.view"],
      },
      {
        title: "Stock Count",
        path: "/admindashboard/inventory/count",
        permissions: ["inventory.count"],
      },
      {
        title: "Low Stock",
        path: "/admindashboard/inventory/low-stock",
        permissions: ["inventory.view"],
      },
      {
        title: "Stock Valuation",
        path: "/admindashboard/inventory/valuation",
        permissions: ["inventory.view"],
      },
      {
        title: "Barcode Labels",
        path: "/admindashboard/inventory/barcode-labels",
        permissions: ["barcode_labels.view"],
      },
      { title: 'Online Pricing', path: '/admindashboard/inventory/online-pricing', permissions: ['inventory.adjust'] },
    ],
  },
  {
    title: "Purchases",
    icon: <FiShoppingBag size={20} />,
    children: [
      {
        title: "Overview",
        path: "/admindashboard/purchases",
        permissions: ["purchases.view"],
      },
      {
        title: "Purchase Orders",
        path: "/admindashboard/purchases/orders",
        permissions: ["purchases.view"],
      },
      {
        title: "Create Purchase Order",
        path: "/admindashboard/purchases/create",
        permissions: ["purchases.create"],
      },
      {
        title: "GRN",
        path: "/admindashboard/purchases/grn",
        permissions: ["purchases.receive"],
      },
      {
        title: "Purchase Returns",
        path: "/admindashboard/purchases/returns",
        permissions: ["purchases.view"],
      },
    ],
  },
  {
    title: "Sales",
    icon: <FiDollarSign size={20} />,
    children: [
      {
        title: "Sales Dashboard",
        path: "/admindashboard/sales",
        permissions: ["sales_dashboard.view"],
      },
      {
        title: "Customers",
        path: "/admindashboard/sales/customers",
        permissions: ["customers.view"],
      },
      {
        title: "Support Tickets",
        path: "/admindashboard/sales/support-tickets",
        permissions: ["customer_support.view"],
      },
      {
        title: "Ecommerce Orders",
        path: "/admindashboard/sales/ecommerce-orders",
        permissions: ["ecommerce_orders.view"],
      },
      {
        title: "Sales Orders",
        path: "/admindashboard/sales/orders/list",
        permissions: ["sales_orders.view"],
      },
      {
        title: "POS Bills",
        path: "/admindashboard/sales/pos-bills",
        permissions: ["pos.view_reports"],
      },
      {
        title: "Delivery Challans",
        path: "/admindashboard/delivery/challan/list",
        permissions: ["delivery_challans.view"],
      },
      {
        title: "Sales Invoices",
        path: "/admindashboard/sales/invoice/list",
        permissions: ["sales_invoices.view"],
      },
      {
        title: "Sales Returns",
        path: "/admindashboard/sales/returns",
        permissions: ["sales_returns.view"],
      },
      {
        title: "Credit Notes",
        path: "/admindashboard/sales/credit-notes",
        permissions: ["sales_credit_notes.view"],
      },
    ],
  },
  {
    title: "Marketing",
    icon: <FiTag size={20} />,
    children: [
      {
        title: "Promotions & Coupons",
        path: "/admindashboard/marketing/promotions",
        permissions: ["promotions.view"],
      },
    ],
  },
  {
    title: "HRMS",
    icon: <FiUserCheck size={20} />,
    children: [
      {
        title: "Employees",
        path: "/admindashboard/employees",
        permissions: ["employees.view"],
      },
      {
        title: "Employee Advance Payments",
        path: "/admindashboard/employee-advances",
        permissions: ["employee_advances.view"],
      },
      {
        title: "Add Employee",
        path: "/admindashboard/employees/add",
        permissions: ["employees.create"],
      },
      {
        title: "Attendance Overview",
        path: "/admindashboard/attendance",
        permissions: ["attendance.view_team"],
      },
      {
        title: "Daily Attendance",
        path: "/admindashboard/attendance/daily",
        permissions: ["attendance.view_team"],
      },
      {
        title: "Monthly Attendance",
        path: "/admindashboard/attendance/monthly",
        permissions: ["attendance.view_team"],
      },
      {
        title: "Attendance Corrections",
        path: "/admindashboard/attendance/corrections",
        permissions: ["attendance.manage"],
      },

      {
        title: "Leave Requests",
        path: "/admindashboard/leave",
        permissions: ["leave.approve"],
      },
      {
        title: "Record Employee Leave",
        path: "/admindashboard/leave/apply",
        permissions: ["leave.record_direct"],
      },
      {
        title: "Leave Balances",
        path: "/admindashboard/leave/balance",
        permissions: ["leave.manage_balances"],
      },
      {
        title: "Leave Types",
        path: "/admindashboard/leave/types",
        permissions: ["leave.manage_types"],
      },
      {
        title: "Leave Policies",
        path: "/admindashboard/leave/holiday-policies",
        permissions: ["attendance_holiday_policies.view"],
      },

      {
        title: "Payroll Overview",
        path: "/admindashboard/payroll",
        permissions: ["payroll.view"],
      },
      {
        title: "Generate Payroll",
        path: "/admindashboard/payroll/generate",
        permissions: ["payroll.generate"],
      },
      {
        title: "Off Cycle Payroll",
        path: "/admindashboard/payroll/off-cycle",
        permissions: ["payroll.generate"],
      },
      {
        title: "Payslips",
        path: "/admindashboard/payroll/payslips",
        permissions: ["payslips.view_team"],
      },
      {
        title: "HRMS Reports",
        path: "/admindashboard/hrms-reports",
        permissions: ["hrms.reports.view"],
      },
      {
        title: "Face Machines",
        path: "/admindashboard/attendance/face-machines",
        permissions: [ATTENDANCE_PAGE_PERMISSIONS.faceMachines],
      },
      {
        title: "Punch Review & Approval",
        path: "/admindashboard/attendance/punch-review",
        permissions: [ATTENDANCE_PAGE_PERMISSIONS.punchReview],
      },
    ],
  },
  {
    title: "Analytics",
    icon: <FiBarChart2 size={20} />,
    children: [
      {
        title: "Analytics Dashboard",
        path: "/admindashboard/analytics",
        permissions: ["analytics.view"],
      },
      {
        title: "Sales Analytics",
        path: "/admindashboard/analytics/sales",
        permissions: ["analytics.sales"],
      },
      {
        title: "Customer Analytics",
        path: "/admindashboard/analytics/customers",
        permissions: ["analytics.customers"],
      },
      {
        title: "Inventory Analytics",
        path: "/admindashboard/analytics/inventory",
        permissions: ["analytics.inventory"],
      },
      {
        title: "Purchase Analytics",
        path: "/admindashboard/analytics/purchases",
        permissions: ["analytics.purchases"],
      },
      {
        title: "POS Analytics",
        path: "/admindashboard/analytics/pos",
        permissions: ["analytics.pos"],
      },
      {
        title: "Finance Analytics",
        path: "/admindashboard/analytics/finance",
        permissions: ["analytics.finance"],
      },
      { title: 'GST Analytics', path: '/admindashboard/analytics/gst', permissions: ['analytics.gst'] },
      {
        title: "Employee Analytics",
        path: "/admindashboard/analytics/employees",
        permissions: ["analytics.employees"],
      },
      {
        title: "Attendance Analytics",
        path: "/admindashboard/analytics/attendance",
        permissions: ["analytics.attendance"],
      },
      {
        title: "Payroll Analytics",
        path: "/admindashboard/analytics/payroll",
        permissions: ["analytics.payroll"],
      },
    ],
  },
  {
    title: "Reports",
    icon: <FiFileText size={20} />,
    children: [
      // {
      //   title: "GST Register",
      //   path: "/admindashboard/reports/gst",
      //   permissions: ["reports.gst.view"],
      // },
      {
        title: "Sales Detailed",
        path: "/admindashboard/reports/sales-detailed",
        permissions: ["reports.sales_detailed.view"],
      },
      {
        title: "Sales Tax Register",
        path: "/admindashboard/reports/sales-tax-register",
        permissions: ["reports.sales_tax_register.view"],
      },
      {
        title: "Sales Register Summary",
        path: "/admindashboard/reports/sales-register-summary",
        permissions: ["reports.sales_register_summary.view"],
      },
      { title: "Cashier-wise Report", path: "/admindashboard/reports/cashier-wise", permissions: ["reports.cashier_wise.view"] },
      { title: "Stock Report", path: "/admindashboard/reports/stock", permissions: ["reports.stock.view"] },
      { title: "Stock Ageing", path: "/admindashboard/reports/stock-ageing", permissions: ["reports.stock_ageing.view"] },
      { title: "Inventory Movement Register", path: "/admindashboard/reports/inventory-movement", permissions: ["reports.inventory_movement.view"] },
      { title: "Purchase Return Register", path: "/admindashboard/reports/purchase-returns", permissions: ["reports.purchase_returns.view"] },
      { title: "Online Payment & Refund Register", path: "/admindashboard/reports/online-payments", permissions: ["reports.online_payments.view"] },
      { title: "Customer Loyalty Ledger", path: "/admindashboard/reports/customer-loyalty-ledger", permissions: ["reports.customer_loyalty_ledger.view"] },
      { title: "Gift Card Register", path: "/admindashboard/reports/gift-card-register", permissions: ["reports.gift_card_register.view"] },
      { title: "Stock Adjustment & Count Variance", path: "/admindashboard/reports/stock-adjustment-variance", permissions: ["reports.stock_adjustment_variance.view"] },
      { title: "Season-wise Stock", path: "/admindashboard/reports/season-wise-stock", permissions: ["reports.season_wise_stock.view"] },
      { title: "Size Sell-through & Broken Sizes", path: "/admindashboard/reports/size-sell-through", permissions: ["reports.size_sell_through.view"] },
      { title: "Reorder & Stock Cover", path: "/admindashboard/reports/reorder-stock-cover", permissions: ["reports.reorder_stock_cover.view"] },
      { title: "Dead Stock & Markdown Candidates", path: "/admindashboard/reports/dead-stock", permissions: ["reports.dead_stock.view"] },
      { title: "Brand-wise Trading Performance", path: "/admindashboard/reports/brand-trading", permissions: ["reports.brand_trading.view"] },
      { title: "Counter & Shift Reconciliation", path: "/admindashboard/reports/counter-shift", permissions: ["reports.counter_shift.view"] },
      { title: "GRN Invoice Reconciliation", path: "/admindashboard/reports/grn-reconciliation", permissions: ["reports.grn_reconciliation.view"] },
      { title: "Price Change & GRN Edit Audit", path: "/admindashboard/reports/price-grn-audit", permissions: ["reports.price_grn_audit.view"] },
      { title: "Online Cancellation & Fulfilment", path: "/admindashboard/reports/online-fulfilment", permissions: ["reports.online_fulfilment.view"] },
      { title: "HRMS Missed Punch & Session Exceptions", path: "/admindashboard/reports/attendance-exceptions", permissions: ["reports.attendance_exceptions.view"] },
      { title: "Advance Recovery & Outstanding", path: "/admindashboard/reports/advance-outstanding", permissions: ["reports.advance_outstanding.view"] },
      { title: "Stock Transfer Reconciliation", path: "/admindashboard/reports/transfer-reconciliation", permissions: ["reports.transfer_reconciliation.view"] },
      { title: "Slow-moving Stock", path: "/admindashboard/reports/slow-moving", permissions: ["reports.slow_moving.view"] },
      { title: "Promotion & Coupon Performance", path: "/admindashboard/reports/coupon-performance", permissions: ["reports.coupon_performance.view"] },
      { title: "Customer Retention", path: "/admindashboard/reports/customer-retention", permissions: ["reports.customer_retention.view"] },
      { title: "Barcode & Catalogue Exceptions", path: "/admindashboard/reports/catalogue-exceptions", permissions: ["reports.catalogue_exceptions.view"] },
      { title: "Custom Reports", path: "/admindashboard/reports/custom", permissions: ["custom_reports.view"] },
      { title: "Channel-wise Sales", path: "/admindashboard/reports/channel-wise-sales", permissions: ["reports.channel_wise_sales.view"] },
      { title: "Customer Purchase History", path: "/admindashboard/reports/customer-purchase-history", permissions: ["reports.customer_purchase_history.view"] },
      { title: "Product Profitability", path: "/admindashboard/reports/product-profitability", permissions: ["reports.product_profitability.view"] },
      { title: "Online Product Readiness", path: "/admindashboard/reports/online-product-readiness", permissions: ["reports.online_product_readiness.view"] },
      { title: "Sales Return Register", path: "/admindashboard/reports/sales-returns", permissions: ["reports.sales_returns.view"] },
      { title: "Supplier Purchase & Return Summary", path: "/admindashboard/reports/supplier-purchase-return", permissions: ["reports.supplier_purchase_return.view"] },
      { title: "Discount & Loyalty Redemption", path: "/admindashboard/reports/discount-loyalty", permissions: ["reports.discount_loyalty.view"] },
      { title: "Online Order Detailed", path: "/admindashboard/reports/online-orders-detailed", permissions: ["reports.online_orders_detailed.view"] },
      { title: "Product Wise Sale & Stock", path: "/admindashboard/reports/product-wise-sale-stock", permissions: ["reports.product_wise_sale_stock.view"] },
      { title: "Purchase and Sale-wise", path: "/admindashboard/reports/purchase-sale-wise", permissions: ["reports.purchase_sale_wise.view"] },
      // { title: "GSTR Report", path: "/admindashboard/reports/gstr", permissions: ["reports.gstr.view"] },
      { title: "Purchase Report", path: "/admindashboard/reports/purchase", permissions: ["reports.purchase.view"] },
      { title: "Agent-wise Sales", path: "/admindashboard/reports/agent-wise-sales", permissions: ["reports.agent_wise_sales.view"] },
    ],
  },
  {
    title: "Tools",
    icon: <Toolbox size={20} />,
    children: [
      {
        title: "Dashboard",
        path: "/admindashboard/tools",
        permissions: ["imports.view","audit_logs.view","tools.backup_restore.view","tools.system_jobs.view"],
      },
      {
        title: "Import History",
        path: "/admindashboard/tools/importhistory",
        permissions: ["imports.view"],
      },
      {
        title: "Audit Logs",
        path: "/admindashboard/tools/auditlogs",
        permissions: ["audit_logs.view"],
      },
      {
        title: "Backup & Restore",
        path: "/admindashboard/tools/backuprestore",
        permissions: ["tools.backup_restore.view"],
      },
      {
        title: "System Jobs",
        path: "/admindashboard/tools/systemjobs",
        permissions: ["tools.system_jobs.view"],
      },
    ],
  },
  {
    title: "Settings",
    icon: <FiSettings size={20} />,
    children: [
      {
        title: "Settings",
        path: "/admindashboard/settings",
        permissions: ["settings.view", "auth_sessions.revoke_all"],
      },
      {
        title: "Departments",
        path: "/admindashboard/settings/departments",
        permissions: ["departments.view"],
      },
      {
        title: "Roles & Permissions",
        path: "/admindashboard/settings/roles",
        permissions: ["roles.manage"],
      },
      {
        title: "Branches",
        path: "/admindashboard/settings/branches",
        permissions: ["branches.view"],
      },
      {
        title: "Designations",
        path: "/admindashboard/settings/designation",
        permissions: ["designations.view"],
      },
    ],
  },
];

export default function Sidebar({
  expanded = true,
  onMouseEnter,
  onMouseLeave,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [openMenu, setOpenMenu] = useState("");

  const visibleMenuItems = useMemo(() => {
    return menuItems
      .map((item) => {
        if (!item.children) {
          return !isProductionHiddenTool(item.path || "") && hasAnyPermission(user, item.permissions) ? item : null;
        }

        const children = item.children.filter((child) =>
          !isProductionHiddenTool(child.path) && hasAnyPermission(user, child.permissions),
        );

        return children.length > 0 ? { ...item, children } : null;
      })
      .filter(Boolean);
  }, [user]);

  const activeChild = visibleMenuItems
    .flatMap((item) =>
      (item.children || []).map((child) => ({
        ...child,
        parentTitle: item.title,
      })),
    )
    .filter(
      (child) =>
        location.pathname === child.path ||
        location.pathname.startsWith(`${child.path}/`),
    )
    .sort((a, b) => b.path.length - a.path.length)[0];

  useEffect(() => {
    setOpenMenu(activeChild?.parentTitle || "");
  }, [activeChild?.parentTitle]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  return (
    <aside
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`fixed left-0 top-0 z-50 flex h-screen flex-col overflow-hidden border-r border-gray-100 bg-white transition-[width] duration-200 ${
        expanded ? "w-72" : "w-20"
      }`}
    >
      <div className={`flex h-20 items-center border-b border-gray-100 ${expanded ? "px-6" : "justify-center px-3"}`}>
        <div className="flex w-full items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-700 to-primary-800 shadow-lg shadow-primary-100">
            <img
              src={logo}
              alt="Charms Collections"
              className="h-9 w-9 object-contain"
            />
          </div>

          <div
            className={`flex min-w-[160px] flex-col transition-opacity duration-150 ${
              expanded ? "opacity-100" : "opacity-0"
            }`}
          >
            <h1 className="text-lg font-bold tracking-tight text-gray-900">
              CCPL ERP
            </h1>
            <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.1em] text-primary-700">
              Retail Management Suite
            </p>
          </div>
        </div>
      </div>

      <nav className={`flex-1 overflow-y-auto py-2 ${expanded ? "px-4" : "px-2"}`}>
        <ul className="space-y-2">
          {visibleMenuItems.map((item) => {
            const hasChildren = Boolean(item.children);
            const isOpen = openMenu === item.title;

            if (!hasChildren) {
              return (
                <li key={item.title}>
                  <NavLink
                    to={item.path}
                    end
                    title={!expanded ? item.title : undefined}
                    className={({ isActive }) =>
                      `group flex items-center rounded-2xl py-3.5 transition-all duration-300 ${
                        expanded ? "gap-3 px-4" : "justify-center px-2"
                      } ${
                        isActive
                          ? "bg-primary-50 text-primary-700"
                          : "text-gray-700 hover:bg-primary-50 hover:text-primary-700"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-xl transition-all ${
                            isActive
                              ? "bg-primary-100 text-primary-700"
                              : "bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-700"
                          }`}
                        >
                          {item.icon}
                        </div>

                        <span
                          className={`whitespace-nowrap font-semibold tracking-tight transition-opacity ${
                            expanded ? "opacity-100" : "w-0 overflow-hidden opacity-0"
                          }`}
                        >
                          {item.title}
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            }

            return (
              <li key={item.title}>
                <button
                  type="button"
                  onClick={() =>
                    setOpenMenu((current) =>
                      current === item.title ? "" : item.title,
                    )
                  }
                  title={!expanded ? item.title : undefined}
                  className={`group flex w-full items-center rounded-2xl py-3.5 transition-all duration-200 ${
                    expanded ? "justify-between px-4" : "justify-center px-2"
                  } ${
                    isOpen ? "text-gray-900" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl transition-all ${
                        isOpen
                          ? "bg-gray-100 text-gray-700"
                          : "bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-700"
                      }`}
                    >
                      {item.icon}
                    </div>

                    <span
                      className={`whitespace-nowrap font-semibold tracking-tight transition-opacity ${
                        expanded ? "opacity-100" : "w-0 overflow-hidden opacity-0"
                      }`}
                    >
                      {item.title}
                    </span>
                  </div>

                  <FiChevronDown
                    size={18}
                    className={`text-gray-400 transition-all duration-300 ${
                      expanded ? "opacity-100" : "w-0 overflow-hidden opacity-0"
                    } ${
                      isOpen ? "rotate-180 text-primary-700" : ""
                    }`}
                  />
                </button>

                {expanded && isOpen && (
                  <ul className="ml-5 mt-2 space-y-1 border-l border-gray-100 pl-4">
                    {item.children.map((child) => (
                      <li key={child.path}>
                        <NavLink
                          to={child.path}
                          end
                          className={({ isActive }) =>
                            `flex items-center rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
                              isActive
                                ? "bg-primary-50 text-primary-700 shadow-sm"
                                : "text-gray-500 hover:bg-gray-50 hover:text-primary-700"
                            }`
                          }
                        >
                          {child.title}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={`border-t border-gray-100 ${expanded ? "p-5" : "p-2"}`}>
        <p
          className={`whitespace-nowrap text-base font-semibold text-black transition-opacity ${
            expanded ? "opacity-100" : "h-0 overflow-hidden opacity-0"
          }`}
        >
          Charms Collections
        </p>
        <p
          className={`whitespace-nowrap text-[11px] text-gray-600 transition-opacity ${
            expanded ? "mt-0 opacity-100" : "h-0 overflow-hidden opacity-0"
          }`}
        >
          Retail Management Suite v1.0
        </p>

        <button
          type="button"
          onClick={handleLogout}
          title={!expanded ? "Logout" : undefined}
          className={`flex w-full items-center justify-center gap-2 rounded-lg border border-primary-800/10 bg-primary-800/10 py-2.5 text-sm font-medium text-primary-800 transition-all hover:bg-primary-800 hover:text-white ${
            expanded ? "mt-3 px-4" : "px-2"
          }`}
        >
          <FiLogOut size={16} />
          <span
            className={`whitespace-nowrap transition-opacity ${
              expanded ? "opacity-100" : "w-0 overflow-hidden opacity-0"
            }`}
          >
            Logout
          </span>
        </button>
      </div>
    </aside>
  );
}
